import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadPaymentProof } from '../../api/endpoints/payments';
import type { OpenItemsResponse, PaymentMethod, RecordReceiptRequest } from '../../api/types';
import {
  allocationTotals,
  fillOldestFirst,
  formatMoney,
  inputPaise,
  moneyInputError,
  paiseToDecimal,
  parsePaise,
  type ReceiptAllocationRow,
} from './allocation';
import { formatIstDateTime, istDateTimeInput, istInputToIso } from './paymentDates';
import { ProofInput } from './ProofInput';
import { ProofShareConfirmation } from './ProofShareConfirmation';
import {
  useOpenItemsPaging,
  useSelectedOpenItems,
  type OpenItemsLoader,
} from './useOpenItemsPaging';
import { OpenItemsBrowser } from './OpenItemsBrowser';
import { OrderLabel } from './OrderLabel';
import styles from './Payments.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';

function newRequestId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `payment-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

function receiptRow(
  order: OpenItemsResponse['orders'][number],
  previous?: ReceiptAllocationRow,
): ReceiptAllocationRow {
  return {
    shipmentId: order.shipmentId,
    orderDate: order.invoiceDueDate,
    outstandingAmount: order.outstandingAmount,
    amount: previous?.amount ?? '0.00',
    tdsAmount: previous?.tdsAmount ?? '0.00',
  };
}

function receiptHasValue(row: ReceiptAllocationRow) {
  return (
    inputPaise(row.amount) > 0n ||
    inputPaise(row.tdsAmount) > 0n ||
    Boolean(
      moneyInputError(row.amount.trim() || '0') || moneyInputError(row.tdsAmount.trim() || '0'),
    )
  );
}

export function RecordReceiptDialog({
  openItems: initialItems,
  shipperName,
  initialShipmentId,
  onClose,
  onUploadProof = uploadPaymentProof,
  onLoadItems,
  onSubmit,
  submitting = false,
  claim,
  onRejectClaim,
}: {
  openItems: OpenItemsResponse;
  shipperName: string;
  initialShipmentId?: number;
  onClose: () => void;
  onUploadProof?: (proof: File) => Promise<string>;
  onLoadItems?: OpenItemsLoader;
  onSubmit: (request: RecordReceiptRequest) => void | Promise<void>;
  onRejectClaim?: () => void;
  submitting?: boolean;
  claim?: {
    amount: string;
    reference: string;
    paidAt: string;
    proofUrl?: string;
    proofFeedback?: ReactNode;
    allocations: RecordReceiptRequest['allocations'];
  };
}) {
  const { t } = useTranslation();
  const paging = useOpenItemsPaging(initialItems, onLoadItems);
  const openItems = useMemo(
    () => ({
      ...paging.items,
      orders: paging.items.orders.filter(
        (order) =>
          (initialShipmentId == null || order.shipmentId === initialShipmentId) &&
          (!claim ||
            order.invoiceNumber != null ||
            claim.allocations.some((line) => line.shipmentId === order.shipmentId)),
      ),
    }),
    [paging.items, initialShipmentId, claim],
  );
  const requestId = useRef(newRequestId());
  const [amount, setAmount] = useState(claim?.amount ?? '');
  const [method, setMethod] = useState<PaymentMethod>(claim ? 'OTHER' : 'NEFT');
  const [reference, setReference] = useState(claim?.reference ?? '');
  const [paidAt, setPaidAt] = useState(
    istDateTimeInput(claim ? new Date(claim.paidAt) : undefined),
  );
  const [note, setNote] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [proofSafeToShare, setProofSafeToShare] = useState(false);
  const proofAvailable = claim ? Boolean(claim.proofUrl) : proof != null;
  const [uploadedProof, setUploadedProof] = useState<{ file: File; id: string } | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const createRow = useCallback(
    (order: OpenItemsResponse['orders'][number], previous?: ReceiptAllocationRow) => {
      const line = claim?.allocations.find((item) => item.shipmentId === order.shipmentId);
      return receiptRow(
        order,
        previous ??
          (line
            ? {
                ...line,
                orderDate: order.invoiceDueDate,
                outstandingAmount: order.outstandingAmount,
              }
            : undefined),
      );
    },
    [claim],
  );
  const selection = useSelectedOpenItems(openItems, createRow, receiptHasValue);
  const { rows, setRows } = selection;

  const amountError = amount ? moneyInputError(amount) : null;
  const invalidRow = rows.find(
    (row) =>
      moneyInputError(row.amount.trim() || '0') || moneyInputError(row.tdsAmount.trim() || '0'),
  );
  const safeRows = rows.map((row) => ({
    ...row,
    amount: paiseToDecimal(inputPaise(row.amount)),
    tdsAmount: paiseToDecimal(inputPaise(row.tdsAmount)),
  }));
  const totals = allocationTotals(safeRows, paiseToDecimal(inputPaise(amount)));
  const rowError = safeRows.find(
    (row) => parsePaise(row.amount) + parsePaise(row.tdsAmount) > parsePaise(row.outstandingAmount),
  );
  const uninvoicedClaim =
    claim && [...selection.selected.values()].find(({ order }) => order.invoiceNumber == null);
  const error = uninvoicedClaim
    ? `#${uninvoicedClaim.row.shipmentId} has no current issued invoice. Clear its cash and TDS, or reallocate to an issued invoice.`
    : selection.selected.size > 50
      ? 'A payment can have at most 50 allocations. Clear an allocation before submitting.'
      : invalidRow
        ? `Enter a valid amount for #${invalidRow.shipmentId}.`
        : rowError
          ? `Cash + TDS for #${rowError.shipmentId} exceeds order outstanding.`
          : totals.isOver
            ? 'Total allocated cash exceeds the received amount.'
            : null;
  const valid =
    !amountError &&
    inputPaise(amount) > 0n &&
    reference.trim().length >= 3 &&
    reference.trim().length <= 40 &&
    note.trim().length >= 3 &&
    note.trim().length <= 500 &&
    (claim != null || proof != null) &&
    !error;

  const updateRow = (shipmentId: number, field: 'amount' | 'tdsAmount', value: string) => {
    setRows(rows.map((row) => (row.shipmentId === shipmentId ? { ...row, [field]: value } : row)));
  };

  const submit = async () => {
    if ((!proof && !claim) || !valid || localSubmitting || submitting) return;
    const confirmedSafeProof = proofSafeToShare && proofAvailable;
    setLocalSubmitting(true);
    setSubmitError(null);
    try {
      let proofUploadId = uploadedProof?.file === proof ? uploadedProof.id : null;
      if (!proofUploadId && proof && !claim) {
        proofUploadId = await onUploadProof(proof);
        setUploadedProof({ file: proof, id: proofUploadId });
      }
      await onSubmit({
        clientRequestId: requestId.current,
        shipperId: openItems.counterpartyId,
        amount: paiseToDecimal(parsePaise(amount)),
        method,
        reference: reference.trim(),
        paidAt: istInputToIso(paidAt),
        note: note.trim(),
        proofUploadId: proofUploadId ?? '',
        ...(confirmedSafeProof ? { proofSafeToShare: true } : {}),
        allocations: [...selection.selected.values()].map(({ row }) => ({
          shipmentId: row.shipmentId,
          purpose: 'RECEIPT',
          amount: paiseToDecimal(inputPaise(row.amount)),
          tdsAmount: paiseToDecimal(inputPaise(row.tdsAmount)),
        })),
      });
    } catch (caught) {
      setSubmitError(
        caught instanceof Error ? caught.message : 'We couldn’t complete this payment action.',
      );
    } finally {
      setLocalSubmitting(false);
    }
  };

  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={claim ? 'Verify payment claim' : 'Record receipt'}
        className={styles.dialog}
      >
        <header className={styles.header}>
          <div>
            <h2>{claim ? 'Verify shipper payment claim' : 'Record Shipper Receipt (IN)'}</h2>
            <p>
              {claim
                ? 'Check the submitted transfer and allocate the cash actually received.'
                : initialShipmentId == null
                  ? 'Record one inward transfer and allocate it across outstanding orders.'
                  : `Record cash actually received for order #${initialShipmentId}. Enter the received amount; outstanding is not a transfer amount.`}
            </p>
          </div>
          <div className={styles.actions}>
            <DialogHelp
              context={claim ? 'verify-claim' : 'record-receipt'}
              title={claim ? 'Verify shipper payment claim' : 'Record Shipper Receipt (IN)'}
            />
            {onRejectClaim && (
              <Button
                variant="danger"
                size="sm"
                disabled={submitting || localSubmitting}
                onClick={onRejectClaim}
              >
                {t('payments.recordReceiptDialog.rejectClaim')}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!valid || submitting || localSubmitting || paging.pending}
              onClick={submit}
            >
              {submitting || localSubmitting ? 'Saving…' : claim ? 'Verify claim' : 'Save receipt'}
            </Button>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.card}>
            <div className={styles.grid}>
              <label className={styles.field}>
                {t('payments.recordReceiptDialog.shipper')}
                <input value={shipperName} readOnly />
              </label>
              <label className={styles.field}>
                {t('payments.recordReceiptDialog.receivedAmount')}
                <input
                  aria-label={t('payments.recordReceiptDialog.receivedAmount')}
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
                {amountError && (
                  <span role="alert" className={styles.error}>
                    {amountError}
                  </span>
                )}
              </label>
              <label className={styles.field}>
                {t('payments.recordReceiptDialog.paymentMethod')}
                <select
                  value={method}
                  onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                >
                  {['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'OTHER'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                {t('payments.recordReceiptDialog.bankReference')}
                <input
                  readOnly={Boolean(claim)}
                  aria-label={t('payments.recordReceiptDialog.bankReferenceLabel')}
                  maxLength={40}
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                />
              </label>
              <label className={styles.field}>
                {t('payments.recordReceiptDialog.receivedAt')}
                <DatePicker
                  withTime
                  readOnly={Boolean(claim)}
                  aria-label={t('payments.recordReceiptDialog.paidAtLabel')}
                  value={paidAt}
                  onChange={setPaidAt}
                />
              </label>
              <label className={styles.field}>
                {t('payments.recordReceiptDialog.currentCredit')}
                <input value={formatMoney(parsePaise(openItems.unallocatedCredit))} readOnly />
              </label>
              <label className={`${styles.field} ${styles.full}`}>
                {t('payments.recordReceiptDialog.auditNote')}
                <textarea
                  aria-label={t('payments.recordReceiptDialog.auditNote')}
                  rows={2}
                  maxLength={500}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>
              <div className={styles.full}>
                {claim ? (
                  <div>
                    {claim.proofUrl ? (
                      <a href={claim.proofUrl} target="_blank" rel="noreferrer">
                        {t('payments.recordReceiptDialog.viewSubmittedProof')}
                      </a>
                    ) : (
                      (claim.proofFeedback ?? <p>{t('payments.recordReceiptDialog.proofInDetails')}</p>)
                    )}
                  </div>
                ) : (
                  <ProofInput
                    value={proof}
                    disabled={submitting || localSubmitting}
                    onChange={(file) => {
                      setProof(file);
                      setProofSafeToShare(false);
                      setUploadedProof((current) => (current?.file === file ? current : null));
                      setSubmitError(null);
                    }}
                  />
                )}
                <ProofShareConfirmation
                  checked={proofSafeToShare && proofAvailable}
                  onChange={setProofSafeToShare}
                  disabled={!proofAvailable || submitting || localSubmitting}
                />
              </div>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.sectionTitle}>
              <div>
                <h3>
                  {initialShipmentId == null
                    ? 'Allocate across outstanding orders'
                    : `Allocate to order #${initialShipmentId}`}
                </h3>
                <p>{t('payments.recordReceiptDialog.allocateHint')}</p>
              </div>
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={Boolean(amountError) || inputPaise(amount) <= 0n}
                  onClick={() => {
                    const eligible = claim
                      ? rows.filter((row) =>
                          selection.orders.some(
                            (order) =>
                              order.shipmentId === row.shipmentId && order.invoiceNumber != null,
                          ),
                        )
                      : rows;
                    const filled = new Map(
                      fillOldestFirst(eligible, amount).map((row) => [row.shipmentId, row]),
                    );
                    setRows(rows.map((row) => filled.get(row.shipmentId) ?? row));
                  }}
                >
                  {t('payments.recordReceiptDialog.fillOldestFirst')}
                </Button>
                {(Boolean(amountError) || inputPaise(amount) <= 0n) && (
                  <div className={styles.subtle}>
                    {t('payments.recordReceiptDialog.autoFillHint')}
                  </div>
                )}
              </div>
            </div>
            {initialShipmentId == null && <OpenItemsBrowser paging={paging} />}
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('payments.recordReceiptDialog.colOrder')}</th>
                    <th>{t('payments.recordReceiptDialog.colInvoice')}</th>
                    <th>{t('payments.recordReceiptDialog.colDue')}</th>
                    <th>{t('payments.recordReceiptDialog.colOutstanding')}</th>
                    <th>{t('payments.recordReceiptDialog.colCash')}</th>
                    <th>{t('payments.recordReceiptDialog.colTds')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selection.orders.map((order) => {
                    const row = rows.find((item) => item.shipmentId === order.shipmentId);
                    if (!row) return null;
                    return (
                      <tr key={order.shipmentId}>
                        <td>
                          {selection.selected.has(order.shipmentId) && (
                            <span className={styles.badge}>{t('payments.recordReceiptDialog.selected')}</span>
                          )}
                          <OrderLabel {...order} newTab />
                        </td>
                        <td>{order.invoiceNumber ?? 'Uninvoiced estimate'}</td>
                        <td>{order.invoiceDueDate ?? '—'}</td>
                        <td className={styles.mono}>
                          {formatMoney(parsePaise(order.outstandingAmount))}
                        </td>
                        <td>
                          <input
                            className={styles.moneyInput}
                            aria-label={`Cash for #${order.shipmentId}`}
                            value={row.amount}
                            onChange={(event) =>
                              updateRow(order.shipmentId, 'amount', event.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className={styles.moneyInput}
                            aria-label={`TDS for #${order.shipmentId}`}
                            value={row.tdsAmount}
                            onChange={(event) =>
                              updateRow(order.shipmentId, 'tdsAmount', event.target.value)
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles.footer}>
              <span>
                {t('payments.recordReceiptDialog.allocatedOf', {
                  allocated: formatMoney(totals.totalCashPaise),
                  amount: formatMoney(inputPaise(amount)),
                })}
              </span>
              <span className={styles.credit}>
                {t('payments.recordReceiptDialog.leftAsCredit', { amount: formatMoney(totals.creditPaise) })}
              </span>
            </div>
            {error && (
              <div role="alert" className={styles.error}>
                {error}
              </div>
            )}
            {paidAt && (
              <p>
                {t('payments.recordReceiptDialog.transferTime', { date: formatIstDateTime(istInputToIso(paidAt)) })}
              </p>
            )}
            {submitError && (
              <div role="alert" className={styles.error}>
                {submitError}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
